import { Component, Method, Watch, Listen, State, Host, h, Prop, Event, EventEmitter } from '@stencil/core';

@Component({
  tag: 'jump-search-bar',
  styleUrl: 'jump-search-bar.scss',
  shadow: true,
})
export class JumpSearchBar {

  /* ---------------------- @OWN PROPERTIES ------------------------- */
  jumpSearchBar: HTMLInputElement;
  private componentRef: HTMLElement;

  /* ---------------------- @STATE ------------------------- */
  @State() dropdownVisible: boolean = false;
  @Prop({ mutable: true }) searchValue: string = undefined;

  @Watch('searchValue')
  searchValueChanged(newValue: string) {
    if (newValue === '') {
      // Create a new array reference to trigger re-render
      this.innerResults = [...this.recents];
      this.results = [];
    }
  }

  @State() resType: string = 'recents';
  @State() innerResults: Array<object> = [];
  @State() totalResults: number = 0;
  @State() isExpanded: boolean = false; 

  /* ---------------------- @PROPERTIES ------------------------- */
  @Prop() placeholder: string = 'Cerca...'; //Il testo del placeholder dell\'input della searchbar
  @Prop() noResultText: string = 'Nessun risultato corrispondente'; // Il testo da mostrare in caso nessun risultato sia stato recuperato.
  @Prop() loadingText: string = 'Sto caricando...'; // Il testo mostrato durante il caricamento dei risultati.
  @Prop() resultsText: string = 'risultati'; // Il testo mostrato accanto al numero di risultati totali.
  @Prop() showAllResText: string = 'Mostra tutti i risultati'; // La label da mostrare per il pulsante che mostra tutti i risultati.
  @Prop() identifier: string = 'search-bar'; // Identificatore della searchbar
  @Prop() debounceTime: number = 500; // 'Il tempo di attesa per l\'invio della ricerca in caso di ricerca automatica. In millisecondi.
  @Prop() variant: string = 'outline'; // Scelta dello stile della barra di ricerca tra filled, linear e outline.
  @Prop() withBtn: boolean = false; // Se true, aggiunge un pulsante alla barra di ricerca
  @Prop() recentsTitle: string = 'Recenti'; // Serve per il titolo del dropwdown quando si vedono le ricerche recenti
  @Prop() recents: Array<object> = []; // Serve per il dropdown quando si vedono le ricerche recenti
  @Prop() results: Array<object> = []; // Serve per il dropdown quando si vedono i risultati della ricerca
  @Prop() dropdown: boolean = false; // Se true, mostra il dropdown con i risultati della ricerca.
  @Prop() linearIconOnly: boolean = false; // Se true, mostra solo l'icona nella barra di ricerca lineare
  @Prop() dropdownPosition: string = 'relative'; // Posizione del dropdown rispetto alla searchbar. "relative" o "absolute"


  /* ---------------------- @EVENTS ------------------------- */

  /**
   * Evento emesso quando l'utente clicca sull'icona in variant linear e linearIconOnly
   */
  @Event({ eventName: 'jump-search-bar-expanded' }) searchbarExpanded: EventEmitter;

  /**
   * Evento emesso quando l'utente effettua una ricerca 
   * Emette un oggetto contenenente il valore della ricerca alla chiave 'search'
   * */
  @Event({ eventName: 'jump-search-bar' }) search: EventEmitter;
  @Event({ eventName: 'jump-filterchange' }) searchFilters: EventEmitter; // E' analoga a quella sopra ma serve per i filtri

  /** 
   * Evento emesso quando l'utente clicca sul pulsante (se presente) o nel pulsante del dropdown per vedere tutti i risultati 
   * Emette un oggetto contenenente il valore della ricerca alla chiave 'search'
  */
  @Event({ eventName: 'jump-search-bar-show-all' }) showFullSearch: EventEmitter;

  /* 
  * Evento da ascoltare per recuperare il dato cliccato
  * Emette un oggetto contenente tutti i dati relativi al risultato cliccato.
  */
  @Event({ eventName: 'jump-search-bar-res-clicked'}) resultClicked: EventEmitter; 

  /* ---------------------- @LIFECYCLE ------------------------- */
  componentDidLoad() {
    // Aggiungi l'event listener per il clic al di fuori del componente
    document.addEventListener('click', this.handleOutsideClick.bind(this));
  }

  disconnectedCallback() {
    // Rimuovi l'event listener per evitare perdite di memoria
    document.removeEventListener('click', this.handleOutsideClick.bind(this));
  }

  /* -------------------- @LISTEN ------------------------------- */
  @Listen("jump-search-bar-dropdown-item-connected")
  addOption(e) {
    let props = e.detail; // es {code: 'sku1', imageUrl: 'https://google.com/'}

    // Aggiungi l'elemento solo a `recents`
    this.recents = [...this.recents, { ...props }];

    // Aggiorna `innerResults` basandoti solo su `recents`
    this.innerResults = [...this.recents];
  }


  /* -------------------- @METHOD ------------------------------- */

  /** Imposta i risultati della ricerca all'interno della dropdown */
  @Method()
  async setResults(results, tot?: number) {
    this.results = results;
    this.innerResults = results;
    this.totalResults = tot;
  }

  /* -------------------- LOCAL METHODS ------------------------- */
  handleOutsideClick(event: MouseEvent) {
    // Controlla se il clic è fuori dal componente
    if (this.componentRef && !this.componentRef.contains(event.target as Node)) {
      this.dropdownVisible = false;
    }
  }

  // Gestisce l'evento focus sull'input
  handleFocus = () => {
    const searchBarContainer = this.jumpSearchBar.closest('.SearchBar');
    if (searchBarContainer) {
      searchBarContainer.classList.add('focused');
    }
  };

  // Gestisce l'evento blur sull'input
  handleBlur = () => {
    const searchBarContainer = this.jumpSearchBar.closest('.SearchBar');
    if (searchBarContainer) {
      searchBarContainer.classList.remove('focused');
    }
  };

  doSearch = () => {
    if (this.jumpSearchBar) {
      this.searchValue = this.jumpSearchBar.value;
      
      // Iniziare la ricerca solamente se ci sono almeno tre caratteri 
      if (this.searchValue.length >= 3) {
        this.resType = 'results';

        this.innerResults = undefined;
        
        this.search.emit({ search: this.searchValue });
        this.searchFilters.emit({ name: 's', values: this.searchValue });
  
        // Se il dropdown è chiuso, aprilo. Potrebbe essere chiuso perchè non ci sono recenti
        if (!this.dropdownVisible) {
          this.dropdownVisible = true;
        }
      } else if(this.searchValue.length == 0) {
        this.dropdownVisible = false;
      }
    }
  };

  debounce(fn, delay = this.debounceTime) {
    let timeoutId;

    return (...args) => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
      timeoutId = setTimeout(() => {
        fn.apply(null, args);
      }, delay);
    };
  };

  openDropdown() {
    this.resType = 'recents';
    this.dropdownVisible = true;
  }

  /** Emette l'evento di click su un risultato, mandandone tutti i dati */
  handleResClicked(item) {
    this.resultClicked.emit({ clicked: item });
  } 

  /** Emette l'evento quando l'utente clicca sul pulsante (se presente) o nel pulsante del dropdown per vedere tutti i risultati */
  btnOrShowOtherClicked(){
    this.showFullSearch.emit({ search: this.searchValue });
  }

  /**
   * Se clicco l'icona espando la searchbar ed emetto un evento dedicato
   */
  expandSearchBar() {
    this.isExpanded = true;
    this.searchbarExpanded.emit();
  }


  /* --------------------- RENDER ------------------------------- */
  renderLinearIconOnly() {
    return (
      <Host class={"JumpSearchBar "} ref={(el) => (this.componentRef = el as HTMLElement)}>
        <div class={"SearchBar iconOnly " + this.variant + (this.isExpanded ? " expanded" : "")}>
          <div class="InputWrapper" onClick={(this.isExpanded && this.dropdown && this.recents.length > 0) ? () => this.openDropdown() : null}>
              <jump-icon
                name="magnifying-glass"
                category="light"
                onClick={() => this.expandSearchBar()}
              ></jump-icon>
            {this.isExpanded &&
              // Mostra la barra di ricerca con l'animazione di slide
              <input
                placeholder={this.placeholder}
                type="search"
                value={this.searchValue}
                id={this.identifier}
                class="search-input"
                ref={(el) => { this.jumpSearchBar = el as HTMLInputElement }}
                onInput={this.debounce(this.doSearch)}
                onFocus={this.handleFocus}
                onBlur={this.handleBlur}
              />
            }
          </div>
        </div>
        {(this.dropdown && this.dropdownVisible) && this.renderDropdown()} 
      </Host>
    );
  }

  renderDefault() {
    return (
      <Host class={"JumpSearchBar"} ref={(el) => (this.componentRef = el as HTMLElement)}>
        <div class={"SearchBar " + this.variant + " " + (this.withBtn ? "withBtn" : "") + (this.isExpanded ? " expanded" : "")}>
          <div class="InputWrapper" onClick={(this.dropdown && this.recents.length > 0) ? () => this.openDropdown() : null}>
            {!this.withBtn && <jump-icon name="magnifying-glass" category="light"></jump-icon>}
            <input
              placeholder={this.placeholder}
              type="search"
              id={this.identifier}
              class="search-input"
              value={this.searchValue}
              ref={(el) => { this.jumpSearchBar = el as HTMLInputElement }}
              onInput={this.debounce(this.doSearch)}
              onFocus={this.handleFocus}
              onBlur={this.handleBlur}
            />
          </div>
          {this.withBtn && !this.linearIconOnly && (
            <jump-button
              variant="secondary"
              size="medium"
              name="scopri"
              value="value"
              aria-label="Scopri di più"
              id="button"
              onlyIcon
              onClick={() => this.btnOrShowOtherClicked()}
            >
              <jump-icon slot="suffix" name="magnifying-glass"></jump-icon>
            </jump-button>
          )}
        </div>
        {(this.dropdown && this.dropdownVisible) && this.renderDropdown()} 
      </Host>
    );
  }

  renderDropdown(){
    console.log('POSITION', this.dropdownPosition);
    return <div class={"Dropdown " + (this.dropdownPosition == 'absolute' ? 'dropAbsolute' : 'dropRelative' ) }>
        {(this.resType == 'recents') &&
          <div class="Dropdown__Recents">
            <span class="title">{this.recentsTitle}</span>
            <div class="ResWrapper">
              {this.innerResults.length > 0 && this.innerResults.map((item) => (
                <div onClick={() => this.handleResClicked(item)}>
                  <jump-icon name="magnifying-glass" category="light"></jump-icon>
                  {item['value']}
                </div>
              ))}
            </div>
          </div>
        }
        {this.resType == 'results' &&
          <div class="Dropdown__Results">
            <div class="ResWrapper">
              {this.innerResults === undefined ?
                <div class="helperText"><svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100" preserveAspectRatio="xMidYMid" width="252" height="252" ><g><g transform="rotate(0 50 50)">
                  <rect fill="#707070" height="11" width="4" ry="1.87" rx="1.87" y="27.5" x="48">
                    <animate repeatCount="indefinite" begin="-0.9s" dur="1s" keyTimes="0;1" values="1;0" attributeName="opacity"></animate>
                  </rect>
                </g><g transform="rotate(36 50 50)">
                    <rect fill="#707070" height="11" width="4" ry="1.87" rx="1.87" y="27.5" x="48">
                      <animate repeatCount="indefinite" begin="-0.8s" dur="1s" keyTimes="0;1" values="1;0" attributeName="opacity"></animate>
                    </rect>
                  </g><g transform="rotate(72 50 50)">
                    <rect fill="#707070" height="11" width="4" ry="1.87" rx="1.87" y="27.5" x="48">
                      <animate repeatCount="indefinite" begin="-0.7s" dur="1s" keyTimes="0;1" values="1;0" attributeName="opacity"></animate>
                    </rect>
                  </g><g transform="rotate(108 50 50)">
                    <rect fill="#707070" height="11" width="4" ry="1.87" rx="1.87" y="27.5" x="48">
                      <animate repeatCount="indefinite" begin="-0.6s" dur="1s" keyTimes="0;1" values="1;0" attributeName="opacity"></animate>
                    </rect>
                  </g><g transform="rotate(144 50 50)">
                    <rect fill="#707070" height="11" width="4" ry="1.87" rx="1.87" y="27.5" x="48">
                      <animate repeatCount="indefinite" begin="-0.5s" dur="1s" keyTimes="0;1" values="1;0" attributeName="opacity"></animate>
                    </rect>
                  </g><g transform="rotate(180 50 50)">
                    <rect fill="#707070" height="11" width="4" ry="1.87" rx="1.87" y="27.5" x="48">
                      <animate repeatCount="indefinite" begin="-0.4s" dur="1s" keyTimes="0;1" values="1;0" attributeName="opacity"></animate>
                    </rect>
                  </g><g transform="rotate(216 50 50)">
                    <rect fill="#707070" height="11" width="4" ry="1.87" rx="1.87" y="27.5" x="48">
                      <animate repeatCount="indefinite" begin="-0.3s" dur="1s" keyTimes="0;1" values="1;0" attributeName="opacity"></animate>
                    </rect>
                  </g><g transform="rotate(252 50 50)">
                    <rect fill="#707070" height="11" width="4" ry="1.87" rx="1.87" y="27.5" x="48">
                      <animate repeatCount="indefinite" begin="-0.2s" dur="1s" keyTimes="0;1" values="1;0" attributeName="opacity"></animate>
                    </rect>
                  </g><g transform="rotate(288 50 50)">
                    <rect fill="#707070" height="11" width="4" ry="1.87" rx="1.87" y="27.5" x="48">
                      <animate repeatCount="indefinite" begin="-0.1s" dur="1s" keyTimes="0;1" values="1;0" attributeName="opacity"></animate>
                    </rect>
                  </g><g transform="rotate(324 50 50)">
                    <rect fill="#707070" height="11" width="4" ry="1.87" rx="1.87" y="27.5" x="48">
                      <animate repeatCount="indefinite" begin="0s" dur="1s" keyTimes="0;1" values="1;0" attributeName="opacity"></animate>
                    </rect>
                  </g><g></g></g></svg>{this.loadingText}</div>
                :
                (this.innerResults.length > 0 ?
                  this.innerResults.map((item) => (
                    <div onClick={() => this.handleResClicked(item)} key={item['id']}>
                      {item['img'] && <img src={item['img']} alt="result" />}
                      {item['value']}
                    </div>
                  ))
                  :
                  <div class="helperText">{this.noResultText}</div>
                )
              }
              {(this.innerResults && this.innerResults.length > 0) &&
                <div class="totalRes">
                  <jump-button variant="secondary" size="small" text onClick={() => this.btnOrShowOtherClicked()} target="_blank"> {this.showAllResText} </jump-button>
                  {this.totalResults && <div>{this.totalResults} {this.resultsText}</div>}
                </div>
              }
            </div>
          </div>
        }
      </div>
  }

  render() {
    if (this.variant === 'linear' && this.linearIconOnly) {
      return this.renderLinearIconOnly();
    } else {
      return this.renderDefault();
    }
  }
}